























































































































import Vue from 'vue';
import axios from 'axios';
import {
  Component,
  Prop,
  Watch,
} from 'vue-property-decorator';

import { EventEmitter } from 'events';

import CommonController from '@/controllers/CommonController';
import { talendCommon } from '@/utils/config';
import { openLoadingTab, showTabError } from '@/utils/previewTab';
import formatMb from '@/utils/formatMb';

interface UploadFile {
  file: any;
  name: string;
  tempName: string;
  s3file: S3File | null;
  inEdit: boolean;
  inUpload: boolean;
  lastError: string;
  // SHA-256 содержимого для дедупа. null/undefined — содержимого нет (гидрированные из
  // черновика) или Web Crypto недоступен; тогда участвует только дедуп по имени.
  hash?: string | null;
  // Размер в байтах для лимитов. null — неизвестен (S3 не ответил на открытии черновика).
  size?: number | null;
}

interface S3File {
  bucket: string;
  filename: string;
}

// Бюджет ключа объекта в S3: object_name = varchar(100) (pretension_documents/sheet_documents).
// Длиннее — INSERT упадёт/обрежет (битая ссылка). Блокируем добавление с понятным сообщением.
const MAX_OBJECT_NAME_LENGTH = 100;
// Бэкенд при appendTimestamp=true дописывает _<13-значный timestamp> перед расширением.
const TIMESTAMP_SUFFIX_LENGTH = 14;

@Component
export default class FileLoader extends Vue {
  @Prop({ required: true })
  bucket!: any;

  @Prop({ default: null })
  uploadEventEmitter!: EventEmitter;

  @Prop({ default: '' })
  accept!: string;

  // Показывать иконку-глаз у каждого файла → открыть его в новой вкладке (встроенный вьювер).
  // type: Boolean ОБЯЗАТЕЛЕН — в проекте нет emitDecoratorMetadata, иначе bare-атрибут
  // <file-loader previewable> прилетит как "" (falsy) и иконка не отрендерится.
  @Prop({ type: Boolean, default: false })
  previewable!: boolean;

  /**
   * FML-ltpt.9: уже загруженные в S3 вложения (refs из payload черновика). Гидрируются в files
   * как готовые (s3file заполнен), чтобы при открытии черновика они были видны, удаляемы и
   * дополняемы. Срабатывает по смене ссылки (openForEdit / сброс в create), не затирая
   * добавленные пользователем в текущей сессии файлы.
   */
  @Prop({ default: () => [] })
  initialFiles!: Array<{ bucket: string; objectName: string; hash?: string | null; size?: number | null }>;

  /**
   * Префикс ключа S3 для заливаемых файлов (напр. `attachments/<ns>/`). Если задан — заливаем с
   * appendTimestamp=false и ключом `${uploadKeyPrefix}${имя}` (неймспейс по сущности, без
   * timestamp-постфикса). Пусто (default) — прежнее поведение (appendTimestamp=true), чтобы не
   * ломать прочих потребителей общего FileLoader.
   */
  @Prop({ type: String, default: '' })
  uploadKeyPrefix!: string;

  // Opt-in (§8 п.3): если задан — вместо v-file-input со скрепкой рендерится кнопка,
  // открывающая скрытый input[type=file]. Пусто (default) — прежнее поведение (регресс для
  // прочих потребителей общего FileLoader).
  @Prop({ default: '' })
  readonly attachButtonLabel!: string;

  // Лимиты в байтах на файл и на все файлы вместе; 0 — без лимита. Проверяются при добавлении.
  @Prop({ type: Number, default: 0 })
  readonly maxFileBytes!: number;

  @Prop({ type: Number, default: 0 })
  readonly maxTotalBytes!: number;

  files: Array<UploadFile> = [];

  dummyFiles: Array<any> = [];

  @Watch('initialFiles', { immediate: true })
  onInitialFilesChange(refs: Array<{ bucket: string; objectName: string; hash?: string | null; size?: number | null }>) {
    this.files = (refs || []).map((ref) => ({
      file: null,
      // Отображаемое/сравниваемое имя — basename: objectName несёт путь attachments/<ns>/имя
      // (или легаси-постфикс). Полный ключ хранится в s3file.filename (скачивание/удаление).
      name: this.baseName(ref.objectName),
      tempName: this.baseName(ref.objectName),
      s3file: { bucket: ref.bucket, filename: ref.objectName },
      inEdit: false,
      inUpload: false,
      lastError: '',
      // Хеш из draft_payload → дедуп по содержимому ловит и гидрированные вложения (§4.5).
      hash: ref.hash ?? null,
      size: ref.size ?? null,
    }));
    this.emitFilesState();
    this.emitFilesSelected();
  }

  beforeMount() {
    if (!!this.uploadEventEmitter === true) {
      this.uploadEventEmitter.on('upload', async (callback: () => {}) => {
        await this.loadAll();
        callback();
      });
    }
  }

  beforeDestroy() {
    if (!!this.uploadEventEmitter === true) {
      this.uploadEventEmitter.removeAllListeners();
    }
  }

  get loaded() {
    for (let i = 0; i < this.files.length; i += 1) {
      const file = this.files[i];
      if (file.inEdit === true || file.s3file === null || file.inUpload === true || file.lastError) {
        return false;
      }
    }
    return true;
  }

  // Эмитим bucket/filename + hash содержимого: потребитель (CreatePretension) кладёт hash в
  // draft_payload.attachments[]. В assemble hash вычищается prepareBody.
  get s3files() {
    return this.files
      .filter((v: UploadFile) => v.s3file !== null)
      .map((v: UploadFile) => ({
        bucket: v.s3file!.bucket, filename: v.s3file!.filename, hash: v.hash ?? null, size: v.size ?? null,
      }));
  }

  async loadAll() {
    // Коммитим незавершённые переименования (открыто поле «Имя файла», inEdit=true): пользователь
    // нажал «Сохранить»/«Отправить» с открытым редактированием. Иначе inEdit держит loaded=false и
    // операция падает с «Не все файлы загружены», хотя файл есть. editDone применит tempName.
    this.files.filter((f: UploadFile) => f.inEdit).forEach((f: UploadFile) => this.editDone(f));
    const promiseArray: Array<Promise<any>> = [];
    for (let i = 0; i < this.files.length; i += 1) {
      const file = this.files[i];
      if (this.fileIsDone(file)) {
        continue;
      }
      promiseArray.push(this.uploadFile(file));
    }
    await Promise.all(promiseArray);
  }

  fileIsDone(file: UploadFile): boolean {
    return !(file.inEdit === true || file.s3file === null || file.inUpload === true || file.lastError);
  }

  /**
   * SHA-256 содержимого файла (hex). Возвращает null, если Web Crypto недоступен
   * (не secure context) или чтение не удалось — тогда дедуп по хешу пропускается,
   * остаётся дедуп по имени. Стенды на HTTPS → subtle доступен.
   */
  private async computeHash(file: File): Promise<string | null> {
    try {
      const subtle = (window.crypto || ({} as any)).subtle;
      if (!subtle) return null;
      const buffer = await file.arrayBuffer();
      const digest = await subtle.digest('SHA-256', buffer);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (e) {
      return null;
    }
  }

  // Чистое имя файла для отображения и дедупа: последний сегмент пути (отрезаем неймспейс-
  // префикс attachments/<ns>/), затем переходный fallback — срез легаси-постфикса _<timestamp>
  // для черновиков, созданных до неймспейса (короткие суффиксы вроде report_2024.pdf не трогаем).
  private baseName(name: string): string {
    const last = (name || '').split('/').pop() || '';
    return last.replace(/_\d{10,}(\.[^.]+)$/, '$1');
  }

  // Сериализация обработки: дедуп по имени проверяется ДО `await computeHash`, поэтому два
  // быстрых @change могли бы гоняться за this.files (оба прошли бы проверку имени до push'а).
  // Очередь промисов исключает наложение — пачки обрабатываются строго по очереди.
  private filesQueue: Promise<void> = Promise.resolve();

  filesChanged(files: Array<any> = []): Promise<void> {
    this.filesQueue = this.filesQueue.catch(() => undefined).then(() => this.processFiles(files));
    return this.filesQueue;
  }

  // Двойной дедуп при добавлении файлов: (1) по имени — против всех уже добавленных, включая
  // гидрированные из черновика; (2) по SHA-256 содержимого — против файлов с известным хешем.
  // При совпадении — уведомление и пропуск. Сравнение последовательное: каждый добавляемый
  // сверяется со всеми уже принятыми (в т.ч. из той же пачки).
  // Длина итогового ключа S3 (object_name). Неймспейс: prefix + имя (appendTimestamp=false).
  // Иначе бэкенд допишет _<timestamp> → +TIMESTAMP_SUFFIX_LENGTH.
  private projectedKeyLength(name: string): number {
    return this.uploadKeyPrefix !== ''
      ? (this.uploadKeyPrefix + name).length
      : name.length + TIMESTAMP_SUFFIX_LENGTH;
  }

  // Жёсткая валидация типа НА ДОБАВЛЕНИИ: атрибут accept — лишь фильтр диалога выбора,
  // его обходят через «Все файлы» и drag&drop. Проверка по расширению — консистентно с
  // submit-гейтом hasNonPdfAttachment в CreatePretension. Пустой accept — без ограничений
  // (generic-режим компонента).
  // КОНТРАКТ ПРОПА accept: валидация включается ТОЛЬКО при точном значении
  // 'application/pdf' (единственный текущий потребитель — CreatePretension). Списки MIME /
  // '.ext'-формы валидацией не поддержаны — при появлении такого потребителя расширить
  // isAcceptedType/hasPdfMagicBytes, а не полагаться молча на фильтр диалога.
  private isAcceptedType(name: string): boolean {
    if (this.accept !== 'application/pdf') return true;
    return /\.pdf$/i.test(name);
  }

  // Контент-проверка: расширение подделывается переименованием на диске (docx → .pdf).
  // Настоящий PDF начинается с '%PDF-'. Читаем через FileReader (Blob.arrayBuffer() нет в
  // старых браузерах/jsdom). Сбой чтения → fail-open (расширение уже проверено, PHP/рендер —
  // следующий рубеж) — зеркально guard'у computeHash.
  private hasPdfMagicBytes(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const head = new Uint8Array((reader.result as ArrayBuffer) || new ArrayBuffer(0));
          resolve(String.fromCharCode(...head) === '%PDF-');
        };
        reader.onerror = () => resolve(true);
        reader.readAsArrayBuffer(file.slice(0, 5));
      } catch (e) {
        resolve(true);
      }
    });
  }

  private async processFiles(files: Array<any> = []) {
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (!this.isAcceptedType(file.name)) {
        this.$bus.$emit('notification', `Файл «${file.name}» отклонён — допустимы только PDF-файлы`, 'error');
        continue;
      }
      if (this.maxFileBytes && file.size > this.maxFileBytes) {
        this.$bus.$emit('notification', `Файл «${file.name}» — ${formatMb(file.size)} МБ, Диадок принимает до ${formatMb(this.maxFileBytes)} МБ — сожмите скан и добавьте снова`, 'error');
        continue;
      }
      const total = this.files.reduce((sum: number, v: UploadFile) => sum + (v.size || 0), 0) + file.size;
      if (this.maxTotalBytes && total > this.maxTotalBytes) {
        this.$bus.$emit('notification', `Файл «${file.name}» не добавлен: вложения вместе были бы ${formatMb(total)} МБ, для подписи — не больше ${formatMb(this.maxTotalBytes)} МБ — сожмите сканы`, 'error');
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      if (this.accept === 'application/pdf' && !(await this.hasPdfMagicBytes(file))) {
        this.$bus.$emit('notification', `Файл «${file.name}» не является PDF по содержимому — допустимы только PDF-файлы`, 'error');
        continue;
      }
      if (this.projectedKeyLength(file.name) > MAX_OBJECT_NAME_LENGTH) {
        this.$bus.$emit('notification', `Имя файла «${file.name}» слишком длинное — переименуйте (лимит ключа хранилища ${MAX_OBJECT_NAME_LENGTH} символов)`, 'error');
        continue;
      }
      if (this.files.some((v: UploadFile) => this.baseName(v.name) === this.baseName(file.name))) {
        this.$bus.$emit('notification', `Файл «${file.name}» уже добавлен`, 'warning');
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      const hash = await this.computeHash(file);
      const dupByHash = hash ? this.files.find((v: UploadFile) => !!v.hash && v.hash === hash) : undefined;
      if (dupByHash) {
        this.$bus.$emit('notification', `Файл «${file.name}» по содержимому совпадает с уже добавленным «${dupByHash.name}»`, 'warning');
        continue;
      }
      this.files.push({
        file,
        name: file.name,
        tempName: file.name,
        s3file: null,
        inEdit: false,
        inUpload: false,
        lastError: '',
        hash,
        size: file.size,
      });
    }
    this.dummyFiles = [];
    this.emitFilesState();
    this.emitFilesSelected();
  }

  triggerHiddenInput() {
    const input = this.$refs.hiddenFileInput as HTMLInputElement;
    if (input) {
      input.click();
    }
  }

  onHiddenInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.filesChanged(Array.from(input.files || []));
    input.value = '';
  }

  editFile(file: UploadFile) {
    file.inEdit = !file.inEdit;
    this.emitFilesState();
  }

  /**
   * Открыть файл в новой вкладке (встроенный браузерный вьювер).
   * Локальный (ещё не загруженный) — мгновенно через objectURL. Уже в S3 — вкладку открываем
   * синхронно (popup-safe), затем грузим байты и показываем inline (download-URL форсит скачивание).
   */
  async viewFile(file: UploadFile) {
    if (file.file instanceof File) {
      window.open(URL.createObjectURL(file.file), '_blank', 'noopener');
      return;
    }
    if (!file.s3file) {
      return;
    }
    const tab = openLoadingTab();
    try {
      const url = talendCommon.downloadFileFromS3
        .replace('{authorizationToken}', localStorage.getItem('authToken') ?? '')
        .replace('{bucket}', file.s3file.bucket)
        .replace('{file}', file.s3file.filename);
      const resp = await axios.get(url, { responseType: 'blob' });
      const objUrl = URL.createObjectURL(resp.data);
      if (tab) {
        tab.location.href = objUrl;
      } else {
        window.open(objUrl, '_blank', 'noopener');
      }
    } catch (e) {
      showTabError(tab, 'Не удалось открыть файл');
      this.$bus.$emit('notification', 'Не удалось открыть файл', 'error');
    }
  }

  // Можно ли переименовать: только при наличии локальных байтов. У гидрированных из черновика
  // вложений (file.file === null) байтов нет, а переименование меняет S3-ключ и требует
  // перезаливки — переименовывать их нельзя.
  canRename(file: UploadFile): boolean {
    return !!file.file;
  }

  editDone(file: UploadFile) {
    const next = (file.tempName || '').trim();
    // Всегда выходим из режима правки (inEdit=false): иначе loadAll-автокоммит при сохранении
    // оставит inEdit и заблокирует «Сохранить» («Не все файлы загружены»).
    file.inEdit = false;
    if (!next) {
      // Пустое имя → ключ attachments/<ns>/ без имени файла. Откатываем, не сбрасывая s3file.
      this.$bus.$emit('notification', 'Имя файла не может быть пустым — изменение отменено', 'warning');
      file.tempName = file.name;
    } else if (this.files.some((v: UploadFile) => v !== file && this.baseName(v.name) === this.baseName(next))) {
      // Коллизия имени с другим файлом → один S3-ключ → перезапись. Откатываем.
      this.$bus.$emit('notification', `Файл «${next}» уже добавлен — переименование отменено`, 'warning');
      file.tempName = file.name;
    } else if (!this.isAcceptedType(next)) {
      // Переименование не должно обходить add-time гейт типов (doc.pdf → doc.docx). Откатываем.
      this.$bus.$emit('notification', `«${next}» — допустимы только PDF-файлы, переименование отменено`, 'warning');
      file.tempName = file.name;
    } else if (next !== file.name) {
      file.name = next;
      file.s3file = null; // имя (ключ) изменилось → перезалить под новым ключом
    }
    this.emitFilesState();
    this.emitFilesSelected();
  }

  deleteFile(file: UploadFile) {
    this.files.splice(this.files.findIndex((v: UploadFile) => v === file), 1);
    this.emitFilesState();
    this.emitFilesSelected();
  }

  emitFilesState() {
    this.$emit('change', this.s3files, this.loaded);
  }

  emitFilesSelected() {
    this.$emit('select', this.files.map((f: UploadFile) => f.name));
  }

  async uploadFile(file: UploadFile) {
    file.inUpload = true;
    // Неймспейс по сущности: ключ = `${uploadKeyPrefix}${имя}` с appendTimestamp=false (бэкенд
    // кладёт ключ дословно). Без префикса — прежнее поведение (appendTimestamp=true) для прочих
    // потребителей общего FileLoader.
    const useNamespace = this.uploadKeyPrefix !== '';
    const uploadName = `${this.uploadKeyPrefix}${file.name}`;
    const {
      0: error,
      1: s3file,
    } = (await CommonController.uploadFileToS3(file.file, this.bucket, uploadName, !useNamespace)).result;
    if (error) {
      this.$bus.$emit('notification', `Ошибка загрузки файла: ${file.name} - ${error}`, 'error');
      this.emitFilesState();
      file.inUpload = false;
      file.lastError = error;
      return;
    }
    file.s3file = s3file;
    file.inUpload = false;
    file.lastError = '';
    this.emitFilesState();
  }
}

